/* eslint-disable complexity */
/* eslint-disable max-lines */
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges
} from '@angular/core';
import {
  BookWidgetSearchModalMachCoreComponent,
  RecentListItem
} from '@com-delta-idp/mnx-sitewide-shared-core-ui';
import {
  GenericAction,
  MultiRoutePickerError,
  PredictiveSearch,
  SelectedData,
  Tab,
  ToAndFromAirport,
  ToAndFromAirportCities
} from '@com-delta-omnichannel/mach';
import { AirportCode } from '@com-delta-omnichannel/mach-core-js';
import { Results } from '@com-delta-omnichannel/mach/lib/components/organisms/search-modal';
import {
  PredictiveSearchItemSelectionEventData,
  SearchModalEvent,
  SearchModalEventType,
  TriggerLocation
} from '../../../../models/search-modal-events-object.interface';
import { ErrorStateDirective } from '../../directive';
import {
  createProxyInputForIOSKeyboard,
  focusRoutepicker
} from '../mach-core-utils';

@Component({
  standalone: true,
  selector: 'idp-multicity-route-picker-mach-core',
  templateUrl: './multicity-route-picker-mach-core.component.html',
  styleUrls: ['./multicity-route-picker-mach-core.component.scss'],
  imports: [BookWidgetSearchModalMachCoreComponent, ErrorStateDirective]
})
export class MulticityRoutePickerMachCoreComponent
  implements AfterViewInit, OnChanges, OnDestroy
{
  private lastSearchModalTriggerLocation: TriggerLocation | undefined;
  protected modalOpen = false;
  protected elementId = '';

  @Input() routePickerContent: ToAndFromAirport = {
    origin: {},
    destination: {},
    ariaLabels: {}
  } as ToAndFromAirport;

  @Input() routePickerCities: ToAndFromAirportCities = {
    destinationCode: '',
    destinationName: '',
    destinationRegion: '',
    originCode: '',
    originName: '',
    originRegion: ''
  } as ToAndFromAirportCities;

  @Input() routePickerSearchResult: Results = {} as Results;
  @Input() recentSearchList: RecentListItem[] = [];
  @Input() savedSearchList: string[] = [];
  @Input() searchModalTabs: Tab[] = [];
  @Input() predictiveSearchData!: PredictiveSearch;
  @Input() isFullscreen = false;
  @Input() routeIndex = 0;
  @Input() routePickerError: MultiRoutePickerError[] | undefined = [];

  @Output() swapClicked = new EventEmitter<void>();
  @Output() routePickerClicked = new EventEmitter<SelectedData>();
  @Output() searchModalTriggerClicked = new EventEmitter<SelectedData>();

  @Output() searchModalEvent = new EventEmitter<
    SearchModalEvent<SearchModalEventType>
  >();

  private multicityRoutePicker!: AirportCode;

  private getOriginTrigger() {
    const { origin } = this.routePickerContent;
    return {
      placeholder: origin.title || origin.subtitle || '',
      title: '',
      errorMessage: ''
    };
  }

  private getDestinationTrigger() {
    const { destination } = this.routePickerContent;
    return {
      placeholder: destination.title || destination.subtitle || '',
      title: '',
      errorMessage: ''
    };
  }

  ngAfterViewInit() {
    this.initializeRoutePicker();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['routeIndex']) {
      this.elementId = `multi-route-picker-${this.routeIndex}`;
    }

    if (this.multicityRoutePicker) {
      if (changes['routePickerCities']) {
        this.setRoutePickerValues(this.routePickerCities);
      }

      if (changes['routePickerError']) {
        this.setRoutePickerErrorState();
      }
    }
  }

  protected setRoutePickerErrorState() {
    if (this.multicityRoutePicker) {
      this.multicityRoutePicker.setOriginError(false);
      this.multicityRoutePicker.setDestinationError(false);
      this.multicityRoutePicker.setOriginErrorMessage('');
      this.multicityRoutePicker.setDestinationErrorMessage('');
    }

    if (this.routePickerError?.[this.routeIndex] && this.multicityRoutePicker) {
      const {
        origin,
        destination,
        sameRoute,
        destinationErrorMessage,
        originErrorMessage,
        sameRouteErrorMessage
      } = this.routePickerError[this.routeIndex];
      if (sameRoute) {
        this.multicityRoutePicker.setDestinationError(true);
        this.multicityRoutePicker.setDestinationErrorMessage(
          sameRouteErrorMessage
        );
      } else if (origin) {
        this.multicityRoutePicker.setOriginError(true);
        this.multicityRoutePicker.setOriginErrorMessage(originErrorMessage);

        if (destination) {
          this.multicityRoutePicker.setDestinationError(true);
          this.multicityRoutePicker.setDestinationErrorMessage(
            destinationErrorMessage
          );
        }
      } else if (destination) {
        this.multicityRoutePicker.setDestinationError(true);
        this.multicityRoutePicker.setDestinationErrorMessage(
          destinationErrorMessage
        );
      }
    }
  }

  protected setRoutePickerValues(routePickerCities: ToAndFromAirportCities) {
    if (routePickerCities.originCode) {
      this.multicityRoutePicker.setOriginValue(
        this.routePickerCities.originCode
      );
    }
    if (routePickerCities.destinationCode) {
      this.multicityRoutePicker.setDestinationValue(
        this.routePickerCities.destinationCode
      );
    }
  }

  protected initializeRoutePicker() {
    this.multicityRoutePicker = new AirportCode({
      id: this.elementId,
      destinationTrigger: this.getDestinationTrigger(),
      originTrigger: this.getOriginTrigger(),
      type: 'default'
    });
    this.setupEventHandlers();
    this.setRoutePickerErrorState();
    this.setRoutePickerValues(this.routePickerCities);
  }

  protected setupEventHandlers() {
    this.multicityRoutePicker.onOriginClick(() => {
      const { originCode, originName, originRegion } = this.routePickerCities;
      this.handleRoutePickerClick({
        type: 'origin',
        selectedResult: {
          cityCode: originCode,
          cityName: originName,
          region: originRegion
        }
      });
    });

    this.multicityRoutePicker.onDestinationClick(() => {
      const { destinationCode, destinationName, destinationRegion } =
        this.routePickerCities;
      this.handleRoutePickerClick({
        type: 'destination',
        selectedResult: {
          cityCode: destinationCode,
          cityName: destinationName,
          region: destinationRegion
        }
      });
    });

    this.multicityRoutePicker.onSwap(() => {
      this.swapClicked.emit();
    });
  }

  protected handleModalEvents(event: SearchModalEvent<SearchModalEventType>) {
    this.searchModalEvent.emit(event);
  }

  protected handleRoutePickerClick(data: SelectedData) {
    createProxyInputForIOSKeyboard();
    this.modalOpen = true;
    this.lastSearchModalTriggerLocation = data.type as TriggerLocation;
    this.searchModalEvent.emit({ type: 'modalOpen' });
    this.searchModalTriggerClicked.emit(data);
  }

  protected handlePredictiveSearchItemselection(
    eventData: GenericAction<string>
  ) {
    const data: PredictiveSearchItemSelectionEventData = {
      airportCode: eventData.id,
      airportCity: eventData.value ?? '',
      locationType: this.lastSearchModalTriggerLocation as TriggerLocation,
      flightIndex: this.routeIndex
    };
    this.searchModalEvent.emit({
      type: 'predictiveSearchItemSelected',
      data
    });
  }

  protected handleSearchModalClose(searchText: string) {
    if (searchText) {
      this.handlePredictiveSearchItemselection({
        label: searchText.toUpperCase(),
        id: searchText.toUpperCase(),
        value: ''
      });
    }
    this.modalOpen = false;
    focusRoutepicker(
      this.lastSearchModalTriggerLocation,
      this.multicityRoutePicker
    );
  }

  ngOnDestroy(): void {
    if (this.multicityRoutePicker) {
      this.multicityRoutePicker.destroy();
    }
  }
}
